// Sprint 6 (Phase 2, D48) — Render-time aggregation.
//
// A typical agent turn fires bursts of homogeneous tool calls (e.g. 12 `Read`s
// while exploring a directory, 8 `Edit`s while applying a refactor). Rendering
// each as its own card produces noise, not progress — the founder sees 23
// rows when one summary would suffice.
//
// `aggregateMessages` is a pure render-time pass that walks the topLevel
// message stream and collapses 2+ consecutive `tool_use` events — of any
// mix of tools — into an `AggregationItem`. The original messages are preserved inside the
// item so the developer-mode reveal can drill into each one.
//
// Threshold and behavior decisions taken by Santi (do not reopen):
// - Threshold: 2+ consecutive tool calls within a turn, MIXED TOOLS
//   ALLOWED (sprint compact-action-rows, D1, Santi 2026-08-13). The
//   original rule needed 3+ of the SAME tool: it fired on a homogeneous
//   burst but broke at every tool switch, so on the interleaved turns
//   agents actually produce (Grep, WebSearch, WebSearch, Fetch) it never
//   fired and the founder got the raw list. Verified against the
//   conversation he screenshotted.
// - Subagent tool calls (`Task`, `Agent`) are NEVER aggregated — they have
//   their own `SubagentPanel` and aggregating them would hide the subagent
//   shell entirely.
// - Aggregation is broken by ANY non-`tool_use` message (assistant_text,
//   thinking_block, system_event, unpaired tool_result, etc.). A new
//   assistant turn naturally resets via the assistant_text/thinking_block
//   between turns.
// - A different tool name does NOT break the run (constraint retired
//   2026-09-05, D1, per Santi chat 2026-08-13): it broke the run at every
//   tool switch, so on an interleaved turn the pass never fired at all.
//   Only a non-`tool_use` message, a subagent shell, or the live trailing
//   call ends a run.
// - Sprint work-segment-collapse (D5, D013-EC-522): in founder mode,
//   `thinking_block` items are filtered out of the render set upstream, in
//   `ConversationList.tsx`'s `filtered` pass, before this function ever
//   sees them. The "any non-`tool_use` message breaks the run" rule above
//   is UNCHANGED — this file's logic did not move — but it no longer FIRES
//   on thinking rows in founder mode, since they never reach `topLevel`
//   here. Developer mode is unaffected: thinking rows still arrive and
//   still break runs there.

import type { Message } from '../../types';
import { asToolUse, SUBAGENT_TOOL_NAMES, WORKFLOW_TOOL_NAMES } from '../messages';
import { longestCommonDir } from './path-utils';
import { isPlausiblyLive, type RowLivenessCtx } from './founder-layer';

/** 2+ consecutive tool calls in a turn → aggregate (D1). */
export const AGGREGATION_THRESHOLD = 2;

/** `toolName` of a run that mixes tools — the card resolves a generic icon. */
export const MIXED_TOOL_NAME = 'mixed';

export type AggregationToolKind =
  | 'Read'
  | 'Grep'
  | 'Glob'
  | 'Edit'
  | 'Write'
  | 'MultiEdit'
  | 'Bash'
  | 'WebFetch'
  | 'WebSearch'
  | 'NotebookEdit'
  | 'TodoWrite'
  | 'Skill'
  | 'PushNotification'
  | string;

export interface AggregationItem {
  kind: 'aggregation';
  /** Stable key for React reconciliation (id of first item). */
  key: string;
  /** Tool name shared by all items (e.g. `Read`), or `MIXED_TOOL_NAME`
   *  when the run mixes tools — see `toolNames` for the full set. */
  toolName: AggregationToolKind;
  /** Headline already humanized: `Explored 12 files in src/auth`. */
  headline: string;
  /** Number of aggregated tool_use events. */
  count: number;
  /** Number of tool_use events that have an error tool_result paired. */
  errorCount: number;
  /** Number of tool_use events with no tool_result that are still
   * PLAUSIBLY LIVE per the canonical rule in founder-layer.ts
   * (sprint orphan-tool-rows-honest, D013-EC-147). Pre-sprint this
   * counted bare unpaired rows, which kept group spinners alive
   * forever after a mid-tool process death. */
  runningCount: number;
  /** Original tool_use Messages, in order. */
  items: Message[];
  /** Sprint compact-action-rows (D2) — distinct tool names in the run, in
   *  first-seen order. Length 1 ⇒ homogeneous ⇒ the specific headline is
   *  kept; >1 ⇒ the generic `N actions` copy. */
  toolNames: string[];
  /** Wall-clock ms of the FIRST call in the run (D5/D6). */
  startedAtMs: number | null;
  /** Wall-clock ms of the last settled result, or of the last call when
   *  nothing settled. Drives both the clock time and the duration. */
  lastActivityAtMs: number | null;
  /** Total elapsed of the run, null when it cannot be derived (D6). */
  durationMs: number | null;
}

export interface SingleItem {
  kind: 'single';
  message: Message;
}

export type RenderItem = SingleItem | AggregationItem;

export interface AggregateInput {
  topLevel: Message[];
  /** Map from tool_use_id → its tool_result Message, when present. */
  resultByToolUseId: Map<string, Message>;
  /** Sprint orphan-tool-rows-honest (D013-EC-147, spec D3) — per-message
   * plausibly-live context for unpaired rows. Omitted = legacy behavior
   * (unpaired counts as running); ConversationList always passes it. */
  ctxForMessage?: (m: Message) => RowLivenessCtx;
}

/** Public entry point. Pure. */
export function aggregateMessages(input: AggregateInput): RenderItem[] {
  const out: RenderItem[] = [];
  let run: Message[] = [];

  /**
   * D3 — the action still running is never inside a group. "What is it
   * doing right now" is the founder's first question
   * (`founder-first-vision.md` §2), so the live trailing call renders as
   * its own row and joins the group only once it settles. Liveness uses
   * the canonical rule (`isPlausiblyLive`, D013-EC-147), never a bare
   * "has no result yet".
   */
  const splitLiveTail = (items: Message[]): { settled: Message[]; live: Message[] } => {
    const last = items[items.length - 1];
    if (!last) return { settled: items, live: [] };
    const tu = asToolUse(last);
    const settledResult = tu.tool_use_id
      ? input.resultByToolUseId.get(tu.tool_use_id)
      : undefined;
    if (settledResult) return { settled: items, live: [] };
    if (!isPlausiblyLive(input.ctxForMessage?.(last))) return { settled: items, live: [] };
    return { settled: items.slice(0, -1), live: [last] };
  };

  const flush = () => {
    if (run.length === 0) return;
    const { settled, live } = splitLiveTail(run);
    if (settled.length >= AGGREGATION_THRESHOLD) {
      out.push(buildAggregation(settled, input.resultByToolUseId, input.ctxForMessage));
    } else {
      for (const m of settled) out.push({ kind: 'single', message: m });
    }
    for (const m of live) out.push({ kind: 'single', message: m });
    run = [];
  };

  for (const m of input.topLevel) {
    if (m.type !== 'tool_use') {
      flush();
      out.push({ kind: 'single', message: m });
      continue;
    }
    const tu = asToolUse(m);
    if (SUBAGENT_TOOL_NAMES.has(tu.name) || WORKFLOW_TOOL_NAMES.has(tu.name)) {
      // Never aggregate subagent/Workflow shells; they own their own
      // visual block. Sprint d028-workflow-agents (D2, pre-mortem #4) —
      // a Workflow row swallowed into an "N acciones" card would never
      // mount its panel; the identical trade SUBAGENT_TOOL_NAMES already
      // makes (aggregate.ts:21-23) — bounded, known degradation: a
      // run-breaker splits its neighbours (M5, `Bash, Workflow, Bash`
      // test).
      flush();
      out.push({ kind: 'single', message: m });
      continue;
    }
    // D1 — a different tool name no longer breaks the run.
    run.push(m);
  }
  flush();
  return out;
}

function timestampMs(m: Message | undefined): number | null {
  if (!m) return null;
  const parsed = Date.parse(m.timestamp);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildAggregation(
  items: Message[],
  resultByToolUseId: Map<string, Message>,
  ctxForMessage?: (m: Message) => RowLivenessCtx,
): AggregationItem {
  let errorCount = 0;
  let runningCount = 0;
  const filePaths: string[] = [];
  const toolNames: string[] = [];
  let lastActivityAtMs: number | null = null;
  for (const m of items) {
    const tu = asToolUse(m);
    if (!toolNames.includes(tu.name)) toolNames.push(tu.name);
    const tr = resultByToolUseId.get(tu.tool_use_id);
    // D5/D6 — the run's clock/duration end is the last thing that
    // actually happened: a settled result when there is one, otherwise
    // the call itself.
    const endOfThisCall = timestampMs(tr) ?? timestampMs(m);
    if (endOfThisCall !== null && (lastActivityAtMs === null || endOfThisCall > lastActivityAtMs)) {
      lastActivityAtMs = endOfThisCall;
    }
    if (!tr) {
      // D013-EC-147 — the SAME rule the individual rows use; an orphaned
      // child must not keep the group spinner alive.
      if (isPlausiblyLive(ctxForMessage?.(m))) runningCount += 1;
    } else {
      const isErr = (() => {
        const c = tr.content as { is_error?: unknown } | null;
        return !!c && c.is_error === true;
      })();
      if (isErr) errorCount += 1;
    }
    const fp = readFilePathLike(tu.input);
    if (fp) filePaths.push(fp);
  }
  const commonDir = filePaths.length > 0 ? longestCommonDir(filePaths) : null;
  const homogeneous = toolNames.length === 1;
  const toolName = homogeneous ? (toolNames[0] as string) : MIXED_TOOL_NAME;
  const startedAtMs = timestampMs(items[0]);
  // A run whose ends cannot be parsed shows no duration rather than 0s or
  // NaN (D6). Negative spans (clock drift) are treated the same way.
  const durationMs =
    startedAtMs !== null && lastActivityAtMs !== null && lastActivityAtMs >= startedAtMs
      ? lastActivityAtMs - startedAtMs
      : null;
  return {
    kind: 'aggregation',
    key: `agg-${items[0]?.id ?? 'x'}`,
    toolName,
    // A mixed run has no headline this pure module can honestly build:
    // the copy is founder-facing and must be translated, which needs the
    // render site's translator. `homogeneous === false` is the signal;
    // `AggregationCard` resolves `actionRow.countActions`. Gate 2 caught
    // the first version hardcoding English next to a translated failure
    // label, so an `es` row read `5 actions · 1 falló`.
    headline: homogeneous ? aggregationHeadline(toolName, items.length, commonDir) : '',
    count: items.length,
    errorCount,
    runningCount,
    items,
    toolNames,
    startedAtMs,
    lastActivityAtMs,
    durationMs,
  };
}

function readFilePathLike(input: Record<string, unknown>): string | null {
  for (const key of ['file_path', 'path', 'notebook_path']) {
    const v = input[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  // Glob inputs use `pattern`; we treat patterns as paths for the common-dir
  // calculation only when they look path-like.
  const pattern = input.pattern;
  if (typeof pattern === 'string' && pattern.includes('/')) return pattern;
  return null;
}

export function aggregationHeadline(
  toolName: string,
  count: number,
  commonDir: string | null,
): string {
  const inDir = commonDir ? ` in ${commonDir}` : '';
  switch (toolName) {
    case 'Read':
      return `Explored ${count} files${inDir || ' across files'}`;
    case 'Grep':
      return `Searched ${count} times`;
    case 'Glob':
      return `Listed ${count} sets of files`;
    case 'Edit':
    case 'MultiEdit':
    case 'Write':
      return `Modified ${count} files${inDir}`;
    case 'Bash':
      return `Ran ${count} commands`;
    case 'WebFetch':
    case 'WebSearch':
      return `Visited ${count} pages`;
    case 'NotebookEdit':
      return `Edited ${count} notebooks${inDir}`;
    case 'TodoWrite':
      return `Updated plan ${count} times`;
    case 'Skill':
      return `Used ${count} skills`;
    case 'PushNotification':
      return `Sent ${count} notifications`;
    default:
      // D013-EC-214: never title-case the raw tool identifier into founder
      // copy (third instance of the class, sibling of headline.ts /
      // verb-map.ts default branches).
      return `Ran ${count} tool calls`;
  }
}
